/**
 * Kimlik ekranlarının ortak çerçevesi.
 *
 * Görsel alan ürün ailesini anlatır; form hangi kimlik route'u açılırsa açılsın
 * yerinde kalır. Carousel yalnız görsel ve anlatı değiştirir, form durumuna
 * dokunmaz.
 */

import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTheme } from '@/lib/theme';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { BrandLockup } from '@/components/BrandLockup';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import './AuthLayout.css';

const SLIDES: Array<{
  src: string;
  eyebrow: TranslationKey;
  title: TranslationKey;
  body: TranslationKey;
  position: string;
}> = [
  {
    src: '/media/auth/led-facade.webp',
    eyebrow: 'auth.slideLedEyebrow',
    title: 'auth.slideLedTitle',
    body: 'auth.slideLedBody',
    position: '56% center',
  },
  {
    src: '/media/auth/vr-arena.webp',
    eyebrow: 'auth.slideVrEyebrow',
    title: 'auth.slideVrTitle',
    body: 'auth.slideVrBody',
    position: '52% center',
  },
  {
    src: '/media/auth/immersive-stage.webp',
    eyebrow: 'auth.slideImmersiveEyebrow',
    title: 'auth.slideImmersiveTitle',
    body: 'auth.slideImmersiveBody',
    position: '54% center',
  },
  {
    src: '/media/auth/ar-experience.webp',
    eyebrow: 'auth.slideArEyebrow',
    title: 'auth.slideArTitle',
    body: 'auth.slideArBody',
    position: '54% center',
  },
];

export default function AuthLayout() {
  const { preference, appearance, setPreference } = useTheme();
  const { t } = useI18n();
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (paused || interacting || reducedMotion) return;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % SLIDES.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [interacting, paused, reducedMotion]);

  const slide = SLIDES[activeSlide]!;

  return (
    <div className="auth">
      <section
        className="auth__visual"
        aria-label={t('auth.visualAria')}
        onMouseEnter={() => setInteracting(true)}
        onMouseLeave={() => setInteracting(false)}
        onFocusCapture={() => setInteracting(true)}
        onBlurCapture={(event) => {
          if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) {
            setInteracting(false);
          }
        }}
      >
        <div className="auth__slides" aria-hidden="true">
          {SLIDES.map((item, index) => (
            <img
              className={`auth__slide ${index === activeSlide ? 'is-active' : ''}`}
              src={item.src}
              style={{ objectPosition: item.position }}
              alt=""
              key={item.src}
            />
          ))}
        </div>
        <div className="auth__visualshade" />

        <BrandLockup className="auth__visualbrand" />

        <div className="auth__visualcopy" key={slide.src}>
          <p className="auth__eyebrow">{t(slide.eyebrow)}</p>
          <h2>{t(slide.title)}</h2>
          <p>{t(slide.body)}</p>
        </div>

        <div className="auth__carousel" aria-label={t('auth.carouselAria')}>
          <div className="auth__dots">
            {SLIDES.map((item, index) => (
              <button
                type="button"
                className={index === activeSlide ? 'is-active' : ''}
                aria-label={`${index + 1}. ${t(item.eyebrow)}`}
                aria-current={index === activeSlide ? 'true' : undefined}
                onClick={() => setActiveSlide(index)}
                key={item.src}
              />
            ))}
          </div>
          <button
            type="button"
            className="auth__pause"
            aria-label={paused ? t('auth.resume') : t('auth.pause')}
            aria-pressed={paused}
            onClick={() => setPaused((current) => !current)}
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </button>
        </div>
      </section>

      <section className="auth__formside">
        <div className="auth__utility">
          <a className="auth__back" href="https://wondeon.com">
            <span aria-hidden="true">←</span>
            {t('common.backToWondeon')}
          </a>
          <div className="auth__utility-actions">
            <LanguageSwitch />
            <button
              type="button"
              className="auth__theme"
              onClick={() => setPreference(preference === 'dark' ? 'light' : preference === 'light' ? 'system' : 'dark')}
              aria-label={`${t('common.theme')}: ${preference === 'system' ? t('common.themeSystem') : appearance === 'dark' ? t('common.themeDark') : t('common.themeLight')}. ${t('common.changeTheme')}`}
            >
              <AuthThemeIcon mode={preference === 'system' ? 'system' : appearance} />
            </button>
          </div>
        </div>

        <div className="auth__panel">
          <Outlet />
        </div>

        <nav className="auth__foot" aria-label={t('common.legalLinks')}>
          <span>Wondeon</span>
          <Link to="/kvkk-aydinlatma">{t('common.privacy')}</Link>
          <Link to="/kullanim-kosullari">{t('common.terms')}</Link>
        </nav>
      </section>
    </div>
  );
}

function PauseIcon() {
  return <svg viewBox="0 0 20 20" width="17" height="17" fill="none" aria-hidden="true"><path d="M7 5v10M13 5v10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>;
}

function PlayIcon() {
  return <svg viewBox="0 0 20 20" width="17" height="17" fill="none" aria-hidden="true"><path d="m7 5 8 5-8 5V5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>;
}

function AuthThemeIcon({ mode }: { mode: 'dark' | 'light' | 'system' }) {
  if (mode === 'system') return <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" /><path d="M10 3a7 7 0 0 1 0 14Z" fill="currentColor" /></svg>;
  if (mode === 'dark') return <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true"><path d="M15.8 12.4A6.7 6.7 0 0 1 7.6 4.2 6.8 6.8 0 1 0 15.8 12.4Z" stroke="currentColor" strokeWidth="1.5" /></svg>;
  return <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="3.2" stroke="currentColor" strokeWidth="1.5" /><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.3 4.3l1.4 1.4M14.3 14.3l1.4 1.4M15.7 4.3l-1.4 1.4M5.7 14.3l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
}
